/**
 * Shared pose-sequence shape for practice player + Teach builder.
 * Step: { poseKey, holdSeconds, side?, cue, breath, tip?, mod? }
 */

import { resolvePoseRef, lookupPose } from './poseImages';

export const DEFAULT_HOLD_SECONDS = 45;

/** Hard ceiling for a single pose timer (seed + Teach editor). */
export const MAX_HOLD_SECONDS = 420;

/** Longer default holds by style when cue blocks omit time. */
export const STYLE_DEFAULT_HOLD = {
  yin: 180,
  restorative: 150,
  iyengar: 75,
  hatha: 60,
  ashtanga: 30,
  vinyasa: 25,
  kundalini: 40,
  playful: 30,
};

export function defaultHoldForStyle(style) {
  const key = String(style || '').toLowerCase();
  return STYLE_DEFAULT_HOLD[key] || DEFAULT_HOLD_SECONDS;
}

export function normalizeStep(raw, { style } = {}) {
  if (!raw || typeof raw !== 'object') return null;
  const poseKey = String(raw.poseKey || raw.pose || '').trim();
  if (!poseKey) return null;
  let holdSeconds = Number(raw.holdSeconds);
  if (!Number.isFinite(holdSeconds) || holdSeconds <= 0) {
    holdSeconds = defaultHoldForStyle(style);
  }
  holdSeconds = Math.min(MAX_HOLD_SECONDS, Math.max(5, Math.round(holdSeconds)));
  const side = raw.side === 'left' || raw.side === 'right' ? raw.side : undefined;
  const tip = String(raw.tip || '').trim();
  const mod = String(raw.mod || '').trim();
  return {
    poseKey,
    holdSeconds,
    ...(side ? { side } : {}),
    cue: String(raw.cue || raw.say || '').trim(),
    breath: String(raw.breath || '').trim(),
    ...(tip ? { tip } : {}),
    ...(mod ? { mod } : {}),
  };
}

export function normalizeSequence(steps, { style } = {}) {
  if (!Array.isArray(steps)) return [];
  return steps.map((s) => normalizeStep(s, { style })).filter(Boolean);
}

export function sequenceTotalSeconds(steps) {
  return normalizeSequence(steps).reduce((sum, s) => sum + s.holdSeconds, 0);
}

export function sequenceDurationMins(steps) {
  const secs = sequenceTotalSeconds(steps);
  if (!secs) return 0;
  return Math.max(1, Math.round(secs / 60));
}

export function sequencePoseCount(steps) {
  return normalizeSequence(steps).length;
}

/** Resolve display name for a step’s poseKey. */
export function stepPoseLabel(step) {
  if (!step?.poseKey) return 'Pose';
  const pose = lookupPose(step.poseKey);
  if (pose?.displayName) {
    return step.side
      ? `${pose.displayName} (${step.side})`
      : pose.displayName;
  }
  const img = resolvePoseRef(step.poseKey);
  const base = img?.alt || step.poseKey;
  return step.side ? `${base} (${step.side})` : base;
}

/** Lightweight cue markup regenerated from a sequence (compat / future voice). */
export function sequenceToCues(steps, { teacher = false } = {}) {
  const seq = normalizeSequence(steps);
  if (!seq.length) return '';
  return seq
    .map((s, i) => {
      const mins = Math.max(1, Math.round(s.holdSeconds / 60));
      const title = stepPoseLabel(s);
      const lines = [
        `## ${title} · ~${mins} min`,
        teacher ? 'Hold and cue from the room.' : 'Settle into the shape.',
        `**Pose:** ${s.poseKey}`,
      ];
      if (s.cue) lines.push(`**Say:** ${s.cue}`);
      if (s.breath) lines.push(`**Breath:** ${s.breath}`);
      if (s.tip) lines.push(`**Tip:** ${s.tip}`);
      if (s.mod) lines.push(`**Mod:** ${s.mod}`);
      return lines.join('\n');
    })
    .join('\n\n---\n\n');
}

/** Parse inhale/exhale seconds from breath text; defaults 4 / 6. */
export function parseBreathCycle(breathText) {
  const text = String(breathText || '');
  const inhale = text.match(/inhale[^0-9]*(\d+)/i);
  const exhale = text.match(/exhale[^0-9]*(\d+)/i);
  const counts = [...text.matchAll(/(\d+)\s*counts?/gi)].map((m) => Number(m[1]));
  let inSec = inhale ? Number(inhale[1]) : (counts[0] || 4);
  let outSec = exhale ? Number(exhale[1]) : (counts[1] || counts[0] || 6);
  if (!Number.isFinite(inSec) || inSec < 2) inSec = 4;
  if (!Number.isFinite(outSec) || outSec < 2) outSec = 6;
  return { inhaleSeconds: Math.min(12, inSec), exhaleSeconds: Math.min(16, outSec) };
}
